import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import KeyPointsBox from '../../components/news/KeyPointsBox';
import CommentBox from '../../components/news/CommentBox';
import SourceCard from '../../components/news/SourceCard';
import RelatedCards from '../../components/news/RelatedCards';
import data from '../../data/news-articles.json';

/* ------------------------------------------------------------------ *
 * 自動生成記事レンダラ（報道メディア型 v2 文法・データ駆動）。
 * 手書きの downton-abbey-grand-finale/page.tsx と同じ記事文法を
 * news-articles.json から描画する。帯（ArticleSectionBand）は使わない。
 * ------------------------------------------------------------------ */

type Section = { heading: string; paragraphs: string[]; en?: string };
type SourceShotData = { path: string; media: string; url: string; alt?: string; afterSection?: number };
type ExtraSource = { label: string; url: string };

type Article = {
  slug: string;
  lang: 'ja' | 'en';
  title: string;
  dateISO: string;
  sourceUrl: string;
  sourceName: string;
  sections: Section[];
  heroImage?: string;
  heroAlt?: string;
  lead?: string;
  category?: string;
  subtitle?: string;
  keyPoints?: string[];
  sourceShots?: SourceShotData[];
  extraSources?: ExtraSource[];
};

const articles = data as Article[];

const PERSPECTIVE_HEADINGS = ['全日本執事協会の視点', 'Japan Butler Association perspective'];

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

/* 金の短罫 + 任意の英語アイブロウ + 見出し（帯は全廃） */
function SectionHeading({ id, en, children }: { id: string; en?: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      {en && (
        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      )}
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

/* 出典元サイトの実スクショ引用（figure + 出典URLキャプション） */
function SourceShot({ shot, en }: { shot: SourceShotData; en: boolean }) {
  return (
    <figure className="my-9">
      <img src={shot.path} alt={shot.alt || shot.media} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        {en ? 'Source' : '出典'}: {shot.media}（<a href={shot.url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{shot.url}</a>）
      </figcaption>
    </figure>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const location = useLocation();
  const lang: Article['lang'] = location.pathname.startsWith('/en/') ? 'en' : 'ja';
  const en = lang === 'en';
  const article = articles.find((x) => x.slug === slug && x.lang === lang);
  if (!article) return <Navigate to={en ? '/en/news' : '/news'} replace />;

  const hero = article.heroImage || '/images/generated/jba-fs-104-news.webp';
  const category = article.category || (en ? 'Industry' : '業界動向');
  const homeHref = en ? '/en/' : '/';
  const newsHref = en ? '/en/news' : '/news';

  const breadcrumbs = en
    ? [{ label: 'Home', href: homeHref }, { label: 'News', href: newsHref }, { label: article.title }]
    : [{ label: 'ホーム', href: homeHref }, { label: 'ニュース・お知らせ', href: newsHref }, { label: article.title }];

  // 協会の視点（コメント）を本文フローから分離し、末尾の CommentBox で見せる
  const contentSections = article.sections.filter((s) => !PERSPECTIVE_HEADINGS.includes(s.heading));
  const perspective = article.sections.find((s) => PERSPECTIVE_HEADINGS.includes(s.heading));
  const lastIdx = Math.max(contentSections.length - 1, 0);

  // 出典スクショを各セクション直後へ差し込む（afterSection 指定が無ければ配列順に割当）
  const shots = article.sourceShots || [];
  const shotsBySection = new Map<number, SourceShotData[]>();
  shots.forEach((shot, k) => {
    const idx = Math.min(shot.afterSection ?? k, lastIdx);
    const arr = shotsBySection.get(idx) || [];
    arr.push(shot);
    shotsBySection.set(idx, arr);
  });

  // 目次（本文セクションから生成）
  const toc = contentSections.map((s, i) => ({ id: `s-${i}`, label: s.heading }));

  // 末尾の出典一覧
  const sourceRows = [
    { label: en ? 'Source' : '出典元', value: article.sourceName, link: false },
    { label: en ? 'Article URL' : '記事URL', value: article.sourceUrl, link: true },
    { label: en ? 'Published' : '発信日', value: article.dateISO, link: false },
    ...(article.extraSources || []).map((s) => ({ label: s.label, value: s.url, link: true })),
    { label: en ? 'Accessed' : 'アクセス日', value: en ? '2026-07' : '2026年7月', link: false },
  ];

  // 関連記事＝同言語の他の生成記事から回遊導線を作る（内部リンク）
  const related = articles
    .filter((x) => x.lang === lang && x.slug !== article.slug)
    .slice(0, 2)
    .map((x) => ({
      to: en ? `/en/news/${x.slug}` : `/news/${x.slug}`,
      image: x.heroImage || hero,
      aspect: 'aspect-[4/3]',
      alt: x.heroAlt || x.title,
      title: x.title,
      desc: x.lead ? (x.lead.length > 60 ? x.lead.slice(0, 58).trimEnd() + '…' : x.lead) : '',
    }));

  return (
    <Layout>
      {/* フルブリード画像ビルボード（帯なし・ArticleHero と同規格） */}
      <section className="relative min-h-[500px] lg:min-h-[580px] flex items-end bg-[#1a2942] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${hero}')` }} role="img" aria-label={article.heroAlt || article.title} />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(26,41,66,0.38)_0%,rgba(10,12,14,0.86)_100%)]" />
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 pb-16 pt-32 lg:pt-36">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-[11px] text-[#d7b779]">{en ? 'News / Industry' : 'News / 業界動向'}</span>
            <span className="inline-block border border-[#a38e65]/70 text-[#d7b779] text-[11px] tracking-[0.16em] px-4 py-1 font-['Noto_Serif_JP',serif]">{category}</span>
          </div>
          <h1 className="jba-display font-normal text-white text-[clamp(28px,3.6vw,46px)] leading-[1.38] tracking-[0.03em] drop-shadow-lg">{article.title}</h1>
          {article.subtitle && (
            <p className="mt-5 text-[#e7e2d8] text-base sm:text-lg font-['Noto_Serif_JP',serif] leading-[1.9] drop-shadow max-w-3xl">{article.subtitle}</p>
          )}
          <time dateTime={article.dateISO} className="mt-6 block font-['Playfair_Display',serif] italic text-[#a38e65] text-sm">{article.dateISO}</time>
        </div>
      </section>

      {/* パンくず: ビルボード直後に配置 */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* リード（全幅はここだけ許容） */}
      {article.lead && (
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-6 pt-14">
            <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
              <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
            </div>
          </div>
        </section>
      )}

      {/* 本文（単一エディトリアルカラム 760px）＋ サイドバー（lg以上・sticky） */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            {/* 冒頭ポイント */}
            {article.keyPoints && article.keyPoints.length > 0 && (
              <KeyPointsBox title={en ? 'Key points of this article' : 'この記事のポイント'} points={article.keyPoints} />
            )}

            {contentSections.map((s, i) => (
              <div key={i}>
                <SectionHeading id={`s-${i}`} en={s.en}>{s.heading}</SectionHeading>
                <div className="space-y-6">
                  {s.paragraphs.map((p, j) => (
                    <p className={P} key={j}>{p}</p>
                  ))}
                </div>
                {(shotsBySection.get(i) || []).map((shot, k) => (
                  <SourceShot key={k} shot={shot} en={en} />
                ))}
              </div>
            ))}
          </article>

          {/* サイドバー（lg以上・sticky）: 目次 / メタ / 出典 */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              {toc.length > 0 && (
                <nav aria-label={en ? 'Contents' : '目次'} className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                  <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                  <ul className="space-y-2.5">
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">{en ? 'Published' : '発信日'}</dt><dd className="text-[#333] mt-0.5">{article.dateISO}</dd></div>
                  <div><dt className="text-[#8f7a56]">{en ? 'Source' : '出典元'}</dt><dd className="text-[#333] mt-0.5">{article.sourceName}</dd></div>
                  <div><dt className="text-[#8f7a56]">{en ? 'Category' : 'カテゴリ'}</dt><dd className="text-[#333] mt-0.5">{category}</dd></div>
                  <div>
                    <dt className="text-[#8f7a56]">{en ? 'Source URL' : '出典URL'}</dt>
                    <dd className="mt-0.5 min-w-0 break-all"><a href={article.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[#8f7a56] underline decoration-[#a38e65]/50 hover:text-[#1a2942] transition-colors">{article.sourceUrl}</a></dd>
                  </div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* 協会コメント（＝協会の視点） */}
      {perspective && (
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
            <CommentBox
              eyebrow="From the Association"
              heading={en ? 'Comment from the Japan Butler Association' : '全日本執事協会からのコメント'}
              body={perspective.paragraphs.join('\n')}
            />
          </div>
        </section>
      )}

      {/* 出典一覧 */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading={en ? 'Sources' : '出典'} rows={sourceRows} />
        </div>
      </section>

      {/* 関連記事 */}
      {related.length > 0 && (
        <RelatedCards
          heading={en ? 'Related Articles' : '関連記事'}
          items={related}
          readLabel={en ? 'Read' : '詳しく'}
          backHref={newsHref}
          backLabel={en ? 'Back to News' : 'ニュース・お知らせ一覧へ戻る'}
        />
      )}
    </Layout>
  );
}
